import { useMemo, useState, type FormEvent } from 'react';
import { Plus, RefreshCw, Tags, Trash2 } from 'lucide-react';
import { Carregando, Erro, Vazio } from '../componentes/Estados';
import { Campo, Texto as CampoTexto } from '../componentes/Campos';
import { useAuth } from '../contextos/AuthContext';
import { useProdutos } from '../servicos/produtos';
import { sincronizarCategorias } from '../servicos/mercadolivre';
import {
  categoriaVazia,
  removerCategoria,
  salvarCategoria,
  useCategorias,
  type RascunhoCategoria,
} from '../servicos/categorias';

export function AbaCategorias() {
  const { categorias, carregando, erro } = useCategorias();
  const { produtos } = useProdutos();
  const { perfil: usuario } = useAuth();
  const [editando, setEditando] = useState<string | null>(null);
  const [novo, setNovo] = useState(false);
  const [erroAcao, setErroAcao] = useState<string | null>(null);
  const [sincronizando, setSincronizando] = useState(false);
  const [resultadoSync, setResultadoSync] = useState<string | null>(null);

  async function puxarDoMercadoLivre() {
    setErroAcao(null);
    setResultadoSync(null);
    setSincronizando(true);
    try {
      const r = await sincronizarCategorias();
      setResultadoSync(
        `${r.total} categorias no Mercado Livre · ${r.criadas} novas, ${r.atualizadas} atualizadas.`,
      );
    } catch (e) {
      setErroAcao((e as { message?: string }).message ?? 'Não consegui falar com o Mercado Livre.');
    } finally {
      setSincronizando(false);
    }
  }

  const usoPorCategoria = useMemo(() => {
    const conta: Record<string, number> = {};
    for (const p of produtos) {
      if (p.categoriaId) conta[p.categoriaId] = (conta[p.categoriaId] ?? 0) + 1;
    }
    return conta;
  }, [produtos]);

  const semCategoria = produtos.filter((p) => !p.categoriaId).length;

  if (carregando) return <Carregando />;
  if (erro) return <Erro titulo="Não foi possível carregar as categorias" detalhe={erro} />;

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-md border-l-3 border-est bg-est/10 px-4 py-3 text-sm text-tinta-2">
        A categoria é o que liga o produto à tarifa do marketplace. Puxe a lista do{' '}
        <strong className="text-tinta">Mercado Livre</strong> em vez de digitar: escrito à mão, o
        mesmo nome com um acento diferente viraria outra categoria.
        <span className="mt-1.5 block">
          <strong className="text-tinta">A comissão não se cadastra aqui</strong> — ela depende da
          categoria <em>e do preço</em>. Medido na API: em Acessórios para Veículos o Premium cobra
          17% até R$ 100, 14% entre R$ 150 e R$ 500, e volta a 17% acima disso. Por isso a análise
          consulta a tarifa do preço exato, na hora.
        </span>
      </div>

      {erroAcao && <Erro titulo="Não deu certo" detalhe={erroAcao} />}

      {semCategoria > 0 && categorias.length > 0 && (
        <p className="rounded border-l-3 border-c-res bg-c-res/10 px-3 py-2 text-xs text-tinta-2">
          <strong className="text-tinta">
            {semCategoria} {semCategoria === 1 ? 'mercadoria está' : 'mercadorias estão'} sem
            categoria
          </strong>{' '}
          — sem categoria não dá para consultar a tarifa. Abra a ficha de cada uma e escolha.
        </p>
      )}

      {categorias.length === 0 && !novo ? (
        <Vazio
          icone={<Tags className="size-8" />}
          titulo="Nenhuma categoria cadastrada"
          descricao="Sem categoria, não dá para consultar a tarifa do marketplace e toda análise sai como Dados insuficientes. Puxe a lista do Mercado Livre — é uma chamada só."
          acao={
            <button
              type="button"
              onClick={puxarDoMercadoLivre}
              disabled={sincronizando}
              className="inline-flex items-center gap-1.5 rounded bg-marca px-3 py-1.5 text-sm font-semibold text-marca-tinta hover:opacity-90 disabled:opacity-60"
            >
              <RefreshCw className={`size-4 ${sincronizando ? 'animate-spin' : ''}`} aria-hidden />
              {sincronizando ? 'Puxando…' : 'Puxar do Mercado Livre'}
            </button>
          }
        />
      ) : (
        <>
          <div className="flex flex-wrap items-center justify-end gap-2">
            {resultadoSync && (
              <span className="mr-auto text-xs text-c-via">{resultadoSync}</span>
            )}
            <button
              type="button"
              onClick={puxarDoMercadoLivre}
              disabled={sincronizando}
              className="inline-flex items-center gap-1.5 rounded border border-regua-2 px-3 py-1.5 text-sm hover:border-marca hover:text-marca disabled:opacity-60"
            >
              <RefreshCw className={`size-4 ${sincronizando ? 'animate-spin' : ''}`} aria-hidden />
              {sincronizando ? 'Puxando…' : 'Atualizar do Mercado Livre'}
            </button>
            <button
              type="button"
              onClick={() => {
                setNovo(true);
                setEditando(null);
              }}
              className="inline-flex items-center gap-1.5 rounded bg-marca px-3 py-1.5 text-sm font-semibold text-marca-tinta hover:opacity-90"
            >
              <Plus className="size-4" aria-hidden />
              Nova categoria
            </button>
          </div>

          {novo && (
            <FormularioCategoria
              inicial={categoriaVazia()}
              usuarioId={usuario?.id ?? ''}
              aoConcluir={() => setNovo(false)}
            />
          )}

          <ul className="flex flex-col gap-2">
            {categorias.map((c) => {
              const aberto = editando === c.id;
              const usos = usoPorCategoria[c.id] ?? 0;
              return (
                <li key={c.id} className="rounded-md border border-regua-2 bg-superficie">
                  <div className="flex flex-wrap items-center gap-3 px-4 py-3">
                    <span className={`font-medium ${c.ativo ? '' : 'text-tinta-3 line-through'}`}>
                      {c.nome}
                    </span>
                    {!c.ativo && (
                      <span className="rounded bg-tinta-3/12 px-1.5 py-0.5 text-[0.65rem] font-semibold text-tinta-3">
                        INATIVA
                      </span>
                    )}
                    <span className="text-xs text-tinta-3">
                      {usos === 0
                        ? 'nenhuma mercadoria'
                        : `${usos} ${usos === 1 ? 'mercadoria' : 'mercadorias'}`}
                    </span>

                    <div className="ml-auto flex gap-1.5">
                      <button
                        type="button"
                        onClick={() => setEditando(aberto ? null : c.id)}
                        className="rounded border border-regua-2 px-2.5 py-1 text-xs hover:border-marca hover:text-marca"
                      >
                        {aberto ? 'Fechar' : 'Editar'}
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          setErroAcao(null);
                          try {
                            await removerCategoria(c.id, usos);
                          } catch (e) {
                            setErroAcao(e instanceof Error ? e.message : 'Falhou.');
                          }
                        }}
                        aria-label={`Remover ${c.nome}`}
                        className="rounded border border-regua-2 px-2 py-1 text-tinta-3 hover:border-pend hover:text-pend"
                      >
                        <Trash2 className="size-3.5" aria-hidden />
                      </button>
                    </div>
                  </div>

                  {aberto && (
                    <FormularioCategoria
                      id={c.id}
                      inicial={c}
                      usuarioId={usuario?.id ?? ''}
                      aoConcluir={() => setEditando(null)}
                    />
                  )}
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}

function FormularioCategoria({
  id,
  inicial,
  usuarioId,
  aoConcluir,
}: {
  id?: string;
  inicial: RascunhoCategoria;
  usuarioId: string;
  aoConcluir: () => void;
}) {
  const [d, setD] = useState<RascunhoCategoria>(() => ({ ...inicial }));
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const editar = <K extends keyof RascunhoCategoria>(campo: K, valor: RascunhoCategoria[K]) =>
    setD((x) => ({ ...x, [campo]: valor }));

  async function salvar(e: FormEvent) {
    e.preventDefault();
    if (!d.nome.trim()) {
      setErro('Dê um nome à categoria.');
      return;
    }
    setErro(null);
    setSalvando(true);
    try {
      await salvarCategoria(id ?? null, d, usuarioId);
      aoConcluir();
    } catch (ex) {
      setErro(ex instanceof Error ? ex.message : 'Não foi possível salvar.');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <form
      onSubmit={salvar}
      className="flex flex-col gap-4 border-t border-regua bg-superficie px-4 py-4"
    >
      {erro && <Erro titulo={erro} />}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <CampoTexto
          rotulo="Nome"
          valor={d.nome}
          aoMudar={(v) => editar('nome', v ?? '')}
          dica="Como o marketplace chama."
        />
        <Campo rotulo="Ativa">
          <label className="flex items-center gap-2 py-1.5 text-sm">
            <input
              type="checkbox"
              checked={d.ativo}
              onChange={(e) => editar('ativo', e.target.checked)}
            />
            <span className="text-tinta-2">Aparece na escolha de categoria</span>
          </label>
        </Campo>
        <CampoTexto
          rotulo="Observações"
          valor={d.observacoes}
          aoMudar={(v) => editar('observacoes', v)}
        />
      </div>

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={salvando}
          className="rounded bg-marca px-4 py-1.5 text-sm font-semibold text-marca-tinta hover:opacity-90 disabled:opacity-60"
        >
          {salvando ? 'Salvando…' : 'Salvar categoria'}
        </button>
        <button
          type="button"
          onClick={aoConcluir}
          className="rounded border border-regua-2 px-4 py-1.5 text-sm hover:border-marca"
        >
          Cancelar
        </button>
      </div>
    </form>
  );
}
